import { useTranslation } from 'react-i18next';
import { Field, Panel, PanelSection, Switch } from '@/shared/ui';
import { SegmentedField, TextField, TextareaField } from '@/shared/forms';
import type { ListFilter } from '@/api/generated/schemas';
import type { PlaceholderType } from '@/api/generated/schemas';
import { DestinationSelectorField } from '@/modules/products/venua-planner/components/DestinationSelector';
import { PlaceholderSubcategorySelectorField } from '@/modules/products/venua-planner/components/PlaceholderSubcategorySelector';
import { placeholderStatusOptions } from '../../internal/placeholderEnums';
import { FORM_LABEL_WIDTH } from '../../internal/formLayout';
import type { PlaceholderFormApi } from '../usePlaceholderForm';

/** Sestaví doménový filtr subkategorie z aktuálního typu placeholderu (subkategorie patří pod jeden typ). */
function subcategoryFilters(type: PlaceholderType): ListFilter[] {
  return [{ field: 'type', ids: [type] }];
}

/** Panel (2): název, subkategorie, destinace, zobrazení v programu, kód, poznámka, stav. */
export function PlaceholderIdentityPanel({ form }: { form: PlaceholderFormApi }) {
  const { t } = useTranslation('venua-planner');

  return (
    <Panel variant="filled" title={t('schemes.placeholderForm.sections.identity')}>
      <PanelSection>
        <form.Field name="name">
          {(field) => (
            <TextField
              field={field}
              label={t('schemes.placeholderForm.name')}
              layout="inline"
              labelWidth={FORM_LABEL_WIDTH}
              required
              placeholder={t('schemes.placeholderForm.namePlaceholder')}
              data-testid="placeholder-name"
            />
          )}
        </form.Field>

        <form.Subscribe selector={(state) => state.values.type}>
          {(type) => (
            <form.Field name="subcategory">
              {(field) => (
                <PlaceholderSubcategorySelectorField
                  field={field}
                  mode="single"
                  layout="inline"
                  labelWidth={FORM_LABEL_WIDTH}
                  label={t('schemes.placeholderForm.subcategory')}
                  placeholder={t('schemes.placeholderForm.subcategoryPlaceholder')}
                  extraFilters={subcategoryFilters(type)}
                  testId="placeholder-subcategory"
                />
              )}
            </form.Field>
          )}
        </form.Subscribe>

        <form.Field name="destination">
          {(field) => (
            <DestinationSelectorField
              field={field}
              mode="single"
              layout="inline"
              labelWidth={FORM_LABEL_WIDTH}
              label={t('schemes.placeholderForm.destination')}
              placeholder={t('schemes.placeholderForm.destinationPlaceholder')}
              testId="placeholder-destination"
            />
          )}
        </form.Field>

        <form.Field name="showInProgram">
          {(field) => (
            <Field
              label={t('schemes.placeholderForm.showInProgram')}
              layout="inline"
              labelWidth={FORM_LABEL_WIDTH}
            >
              <Switch
                checked={Boolean(field.state.value)}
                onCheckedChange={(checked) => field.handleChange(checked)}
                label={
                  field.state.value
                    ? t('schemes.placeholderForm.showInProgramOn')
                    : t('schemes.placeholderForm.showInProgramOff')
                }
                data-testid="placeholder-show-in-program"
              />
            </Field>
          )}
        </form.Field>

        <form.Field name="code">
          {(field) => (
            <TextField
              field={field}
              label={t('schemes.placeholderForm.code')}
              layout="inline"
              labelWidth={FORM_LABEL_WIDTH}
              data-testid="placeholder-code"
            />
          )}
        </form.Field>

        <form.Field name="note">
          {(field) => (
            <TextareaField
              field={field}
              label={t('schemes.placeholderForm.note')}
              labelWidth={FORM_LABEL_WIDTH}
              data-testid="placeholder-note"
            />
          )}
        </form.Field>

        <form.Field name="status">
          {(field) => (
            <SegmentedField
              field={field}
              label={t('schemes.placeholderForm.status')}
              labelWidth={FORM_LABEL_WIDTH}
              options={placeholderStatusOptions(t)}
              testId="placeholder-status"
            />
          )}
        </form.Field>
      </PanelSection>
    </Panel>
  );
}
